import { makeStyles, Typography } from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import TrendingFlatIcon from '@material-ui/icons/TrendingFlat';
import {
  boschBlue,
  boschGray,
  boschGreen,
  boschRed,
  boschYellow,
} from '../../modules/theme/boschColors';

/**
 * Semantic colour for the delta indicator.
 *
 * Why these four:
 *   - `success` / `error` mirror the announcement widget's palette so
 *     a "+12% active components" stat reads visually identical to a
 *     positive-news banner.
 *   - `warning` carries the Bosch yellow used for "attention" elsewhere
 *     (alerts, deprecation notices).
 *   - `neutral` is for KPIs where direction doesn't matter (e.g.
 *     "1,234 documents") — renders the delta in plain gray.
 */
export type StatCardTone = 'neutral' | 'success' | 'warning' | 'error';

const TONE_PALETTE: Record<StatCardTone, { delta: string; bg: string; border: string }> = {
  neutral: { delta: boschGray[40], bg: boschGray[95], border: boschGray[90] },
  success: { delta: boschGreen[40], bg: boschGreen[95], border: boschGreen[90] },
  warning: { delta: boschYellow[50], bg: boschYellow[95], border: boschYellow[85] },
  error: { delta: boschRed[40], bg: boschRed[95], border: boschRed[85] },
};

const useStyles = makeStyles(theme => ({
  /*
   * Single-stat KPI tile, sized to look right at width 3 (1/4 column)
   * but scales gracefully up to half / full widths. We pin a min-height
   * so a row of 4 stat-cards stays visually aligned even when one
   * card has a long delta string and another has none.
   */
  root: {
    backgroundColor: '#FFFFFF',
    border: `1px solid ${boschGray[90]}`,
    borderRadius: 6,
    padding: theme.spacing(2, 2, 1.75, 2),
    minHeight: 120,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'box-shadow 0.18s ease',
    '&:hover': {
      boxShadow: `0 2px 8px ${boschGray[85]}30`,
    },
  },
  label: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: boschGray[40],
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  valueRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    flexWrap: 'wrap',
  },
  value: {
    fontSize: '2.25rem',
    fontWeight: 700,
    color: boschBlue[40],
    lineHeight: 1.05,
  },
  unit: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: boschGray[40],
  },
  deltaRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 8px',
    borderRadius: 12,
    fontSize: '0.75rem',
    fontWeight: 700,
    width: 'fit-content',
  },
  deltaIcon: {
    fontSize: 16,
  },
}));

export type StatCardProps = {
  label: string;
  value: string | number;
  unit?: string;
  delta?: string;
  tone?: StatCardTone;
};

/** Decide which arrow icon to render based on the delta string.
 *  Falls back to the flat arrow for ambiguous strings ("stable",
 *  "0%", etc.) so the widget never silently picks "up" or "down". */
const pickDeltaIcon = (delta: string) => {
  const trimmed = delta.trim();
  if (trimmed.startsWith('+') || /↑|▲|↗/.test(trimmed)) {
    return <TrendingUpIcon style={{ fontSize: 16 }} />;
  }
  if (trimmed.startsWith('-') || /↓|▼|↘/.test(trimmed)) {
    return <TrendingDownIcon style={{ fontSize: 16 }} />;
  }
  return <TrendingFlatIcon style={{ fontSize: 16 }} />;
};

/**
 * Single KPI display: small label, big number, optional unit and
 * delta pill. Designed to be the natural companion to the new
 * 1/3 and 1/4 grid widths — four of these in a row at width 3
 * read as a dashboard hero strip.
 *
 * The widget is intentionally STATIC: the `value` and `delta` are
 * authored values, not live queries. For live counters use the
 * existing `catalog-overview` widget, or build a dedicated
 * `live-stat-card` widget that reads from a backend in a future
 * iteration. Keeping this one static means it works for non-catalog
 * metrics (e.g. "Cost saved YoY: $1.2M") without forcing every admin
 * to wire up a data source.
 */
export const StatCard = ({
  label,
  value,
  unit,
  delta,
  tone = 'neutral',
}: StatCardProps) => {
  const classes = useStyles();
  const palette = TONE_PALETTE[tone] ?? TONE_PALETTE.neutral;
  return (
    <div className={classes.root}>
      <Typography component="div" className={classes.label}>
        {label || 'Untitled stat'}
      </Typography>
      <div className={classes.valueRow}>
        <Typography component="span" className={classes.value}>
          {value === '' || value === undefined ? '—' : value}
        </Typography>
        {unit && (
          <Typography component="span" className={classes.unit}>
            {unit}
          </Typography>
        )}
      </div>
      {delta && (
        <div
          className={classes.deltaRow}
          style={{
            color: palette.delta,
            backgroundColor: palette.bg,
            border: `1px solid ${palette.border}`,
          }}
        >
          {pickDeltaIcon(delta)}
          <span>{delta}</span>
        </div>
      )}
    </div>
  );
};
